import { useEffect, useState } from 'react';
import { notify } from '@/lib/notify';
import { getPlatformSettings, setPlatformMaintenance } from '@/lib/admin';
import type { PlatformSettings } from '@/lib/types';
import { humanize } from '@/lib/errors';

/**
 * /admin/settings — platform settings (formerly the /admin landing page).
 * Currently hosts maintenance-mode toggle. Future: feature flags, etc.
 */
export default function AdminSettings() {
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getPlatformSettings()
      .then((s) => {
        if (cancelled) return;
        setSettings(s);
        setMessage(s?.maintenance_message ?? '');
      })
      .catch((err) => { if (!cancelled) setError(humanize(err)); });
    return () => { cancelled = true; };
  }, []);

  async function toggle(enabled: boolean) {
    setBusy(true); setError(null);
    try {
      // Disabling clears the persisted message too — otherwise it ghosts the
      // form on next page-load and confuses admins ("why does my old test
      // message keep coming back?").
      const msgToSend = enabled ? (message || null) : null;
      const next = await setPlatformMaintenance(enabled, msgToSend);
      notify.success(enabled ? 'Maintenance mode on' : 'Maintenance mode off — message cleared');
      setSettings(next);
      if (!enabled) setMessage('');
    } catch (err) { setError(humanize(err)); }
    finally { setBusy(false); }
  }

  async function saveMessageOnly() {
    setBusy(true); setError(null);
    try {
      const next = await setPlatformMaintenance(settings?.maintenance_mode ?? false, message || null);
      notify.success(message ? 'Message saved' : 'Message cleared');
      setSettings(next);
    } catch (err) { setError(humanize(err)); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h2 className="text-2xl font-semibold tracking-tight">Platform settings</h2>
        <p className="text-sm text-muted-foreground">Cross-org configuration controlled by platform admins.</p>
      </header>

      <section className="rounded-lg border border-border bg-card p-4 space-y-3">
        <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Maintenance mode</h3>
        <p className="text-sm text-muted-foreground">
          When enabled, every page renders a red banner and mutating RPCs return a soft error.
        </p>
        <label className="block text-sm space-y-1">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">Message (optional)</span>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-border px-2 py-1 text-sm"
            placeholder="We're upgrading the schedule engine. Back in 15 minutes."
          />
        </label>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex gap-2 flex-wrap">
          {settings?.maintenance_mode ? (
            <button disabled={busy} onClick={() => toggle(false)} className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50">
              {busy ? 'Working…' : 'Disable maintenance mode'}
            </button>
          ) : (
            <button disabled={busy} onClick={() => toggle(true)} className="rounded-md border-2 border-red-300 px-3 py-1.5 text-sm font-medium text-red-900 hover:bg-red-50 disabled:opacity-50">
              {busy ? 'Working…' : 'Enable maintenance mode'}
            </button>
          )}
          <button
            type="button"
            disabled={busy || message === (settings?.maintenance_message ?? '')}
            onClick={saveMessageOnly}
            className="rounded-md border border-border bg-background px-3 py-1.5 text-sm hover:bg-muted/40 disabled:opacity-50"
            title="Save the message without toggling maintenance mode."
          >
            Save message
          </button>
          {message && (
            <button
              type="button"
              disabled={busy}
              onClick={() => setMessage('')}
              className="text-sm text-muted-foreground underline disabled:opacity-50"
            >
              Clear
            </button>
          )}
        </div>
        {settings?.maintenance_mode && (
          <p className="text-xs text-amber-700">Maintenance mode is currently <strong>active</strong>.</p>
        )}
      </section>
    </div>
  );
}
